// deployment_class: customer_env_allowed
/**
 * Shared Ask Kathedral client — the ONE real path to POST /v3/ai/ask for every
 * customer-facing surface (the global Ask overlay, the Executive cockpit's Ask
 * panel, the inline Drill, and the PA-Lock-Up co-pilot). Extracted so no surface
 * can grow a private or canned answer path again
 * ([[feedback_no_canned_ai_replies_ever_all_real_researched_never_hedge_2026_08_01]]).
 *
 * The backend (`src/v3/server/routes/ai.ts`) governs the call — injection screen,
 * canary exfil-tripwire, Aria audit on every call, per-context token budget,
 * grounding, and the context-registry authorization gate. The frontend's only
 * job is to build the exact request body and surface the reply.
 *
 * - `buildAskBody` is the single source of truth for the `/v3/ai/ask` request
 *   shape (guards the 400-on-schema-mismatch class the overlay once hit).
 * - `askKathedral` is the transport (build body → POST → assistant content).
 * - `useAskKathedral` is the React hook that manages pending/error for a caller
 *   that keeps its own message list (the Drill, the PA-Lock-Up panel).
 */
import { useCallback, useState } from 'react';
import { apiFetch } from '../api/api-client-base';

/**
 * Structured inputs for an Ask. The caller passes the backend AIPortalContext
 * string directly (the Drill sends 'dashboard', PA-Lock-Up sends 'partnership')
 * — the shared client does not re-map frontend portal ids, so contexts the
 * global overlay never serves (e.g. 'dashboard') are still reachable here.
 */
export interface AskKathedralArgs {
  /** The backend AIPortalContext, e.g. 'dashboard' · 'partnership' · 'marketplace'. */
  portalContext: string;
  /** The user's question / prompt. */
  prompt: string;
  /** Optional portal-specific grounding hints (the drilled object, the PA state). */
  contextBindings?: Record<string, unknown>;
  /** Required by the backend for portal_context:'partnership'. */
  partnershipId?: string | null;
}

/** The POST /v3/ai/ask request body (backend: `src/v3/server/routes/ai.ts`). */
export interface AskRequestBody {
  portal_context: string;
  user_message: string;
  partnership_id?: string | null;
  context_bindings?: Record<string, unknown>;
}

/** The backend returns the full turn; callers render `assistant_message.content`. */
interface AskResponse {
  assistant_message: { content: string };
}

/**
 * SSOT for the request shape. Optional fields are omitted when empty so the body
 * is exactly what the backend schema requires (no null-noise, no empty bindings).
 */
export function buildAskBody(args: AskKathedralArgs): AskRequestBody {
  return {
    portal_context: args.portalContext,
    user_message: args.prompt,
    ...(args.contextBindings && Object.keys(args.contextBindings).length > 0
      ? { context_bindings: args.contextBindings }
      : {}),
    ...(args.partnershipId != null ? { partnership_id: args.partnershipId } : {}),
  };
}

/** The shared transport: build the body, POST it, return the assistant content. */
export async function askKathedral(args: AskKathedralArgs): Promise<string> {
  const res = await apiFetch<AskResponse>('/v3/ai/ask', {
    method: 'POST',
    body: JSON.stringify(buildAskBody(args)),
  });
  return res.assistant_message.content;
}

/** A turn as the SERVER holds it — the record the model is actually reading from. */
export interface ServerTurn {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
}

export interface OpenConversation {
  conversation: { id: string; portal_context: string; last_message_at: string } | null;
  messages: ServerTurn[];
}

/**
 * Fetch the conversation the model is ALREADY using for this portal.
 *
 * Without this the panel opened empty while the server carried up to 20 prior
 * turns into every answer — so a user believed they were starting fresh, and a
 * reply referencing earlier context read as invention. Showing the real thread is
 * the difference between an answer a CEO can defend and one they cannot account
 * for.
 *
 * `conversation: null` is a normal, expected state (nothing asked here yet), not
 * an error — callers must render it as an empty thread, never as a failure.
 */
export async function fetchOpenConversation(args: {
  portalContext: string;
  partnershipId?: string | null;
}): Promise<OpenConversation> {
  const qs = new URLSearchParams({ portal_context: args.portalContext });
  if (args.partnershipId) qs.set('partnership_id', args.partnershipId);
  return apiFetch<OpenConversation>(`/v3/ai/conversations/open?${qs.toString()}`);
}

/**
 * End the current thread and start a clean one.
 *
 * The predecessor is preserved and attributed server-side; this is not a client
 * -side clear. A local-only reset would be the same lie in reverse — a blank panel
 * over a conversation the model is still carrying.
 */
export async function startNewConversation(args: {
  portalContext: string;
  partnershipId?: string | null;
}): Promise<{ conversation_id: string }> {
  return apiFetch<{ conversation_id: string }>('/v3/ai/conversations/new', {
    method: 'POST',
    body: JSON.stringify({
      portal_context: args.portalContext,
      ...(args.partnershipId ? { partnership_id: args.partnershipId } : {}),
    }),
  });
}

/**
 * The function shape a caller can inject in tests (mirrors the existing
 * `forTestAskFn` seams on the overlay + dashboard Ask panel).
 */
export type AskFn = (args: AskKathedralArgs) => Promise<string>;

export interface UseAskKathedral {
  /** True while an ask is in flight (drives the "Thinking…" affordance). */
  pending: boolean;
  /** The last error message, or null. Cleared at the start of each send. */
  error: string | null;
  /** Send an ask; sets pending/error; resolves with the reply, rejects on failure. */
  send: (args: AskKathedralArgs) => Promise<string>;
}

/**
 * React hook wrapping the shared transport with pending/error state, for a caller
 * that manages its own message list. Pass a test seam (`askFn`) to inject a mock
 * in unit tests — the default is the real `askKathedral`.
 */
export function useAskKathedral(askFn: AskFn = askKathedral): UseAskKathedral {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const send = useCallback(
    async (args: AskKathedralArgs): Promise<string> => {
      setPending(true);
      setError(null);
      try {
        return await askFn(args);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        throw err;
      } finally {
        setPending(false);
      }
    },
    [askFn],
  );
  return { pending, error, send };
}
